import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import { getServerPageGetSubsidyDetail, getServerPageGetSubsidySlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	PageInformationFieldsFragment,
	SubsidyDetailSingleFieldsFragment,
	SubsidyFieldsFragment
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import { SubsidyHeader } from 'modules/subsidy-header/subsidy-header';
import IntroHeader from 'modules/intro-header/intro-header';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { BaseModule } from 'modules/base-module/base-module';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { getFormattedDate } from 'utils/date';
import { ROUTES } from 'constants/routes';

interface ISubsidyDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	subsidyBySlug?: SubsidyFieldsFragment;
	subsidyDetail?: SubsidyDetailSingleFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const SubsidyDetailPage = (props: ISubsidyDetailPageProps): React.JSX.Element => {
	const router = useRouter();
	const { locale } = router;
	const intl = useIntl();
	const { page, PasswordModule } = usePageProtection({
		pageData: props.subsidyBySlug || null,
		pageQuery: () => getServerPageGetSubsidyDetail({ variables: props.variables }, client)
	});

	const formattedDate = props.subsidyBySlug?.updatedAt
		? getFormattedDate(new Date(props.subsidyBySlug?.updatedAt as string), 'd-MMMM-y', locale)
		: '';

	const modules = page?.modules || [];
	const globalModules = props.subsidyDetail?.modules || [];
	const slug = page?.slug;

	return (
		<Layout
			contentId={props?.id}
			typeName={page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'Subsidy' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page?.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none'
						}}
					>
						<IntroHeader module={page.header} slug={slug} />
					</BaseModule>
					<BaseModule>
						{intl.formatMessage({ id: 'modules.bodyText.updatedAt' })}: {formattedDate}
					</BaseModule>
					{!page.hideFromOverview && <SubsidyHeader module={page} />}
					<ModuleBuilder modules={[...modules, ...globalModules]} slug={slug || '/'} />
				</>
			) : (
				<PasswordLayout locale={props.variables?.locale} slug={props.variables?.slug}>
					<PasswordModule />
				</PasswordLayout>
			)}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetSubsidySlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	const paths = slugs?.map((slugObject) => {
		// Remove /subsidies/ prefix since it's already in the file path
		const cleanSlug = slugObject.slug.replace(/^\/subsidies\//, '');

		return {
			params: {
				slug: cleanSlug || '/'
			},
			locale: slugObject?.locale || ctx.defaultLocale
		};
	});

	return {
		paths: paths || [],
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.SUBSIDIES.slug}/${ctx.params.slug}` : '/';
		const variables = {
			slug,
			locale,
			publicationState
		};

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::subsidy.subsidy',
			contentTypeName: 'subsidy',
			fetchPage: () => getServerPageGetSubsidyDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.subsidyBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			},
			neutralStyle: false,
			isSubsite: false
		});

		// If page doesn't exist and is not password protected, return 404
		if (!passwordResult.page && !passwordResult.isPasswordProtected) {
			return {
				notFound: true
			};
		}

		let subsidyDetailAttributes: any = null;
		if (!passwordResult.isPasswordProtected) {
			// Re-fetch detail to extract global modules specifics (could be optimized inside utility later)
			const detailResult = await getServerPageGetSubsidyDetail({ variables }, client);
			subsidyDetailAttributes = detailResult?.props?.data?.subsidyDetail?.data?.attributes;
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				subsidyBySlug: passwordResult.page,
				subsidyDetail: subsidyDetailAttributes,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in subsidy detail', error);

		return {
			notFound: true
		};
	}
};

export default SubsidyDetailPage;
